/**
 * TransmittalForm — outgoing Transmittal cover sheet (AISC §1.7.0 / Form F-1.7.0).
 *
 * Redesigned 2026-05-05 (CR-2026-FE-TRANSMITTAL-FORM-REDESIGN, Class P) onto
 * the PaperForm component library to meet the SFM "True-to-Form, Apple-like"
 * Quality Standard. Source-of-design: D-033 §3.5 (Chesakl Enterprises Form
 * F-1.7.0 Transmittal at sfm-drawing-feature/docs/qms/_source/forms-chesakl-templates/
 * F1.7.0TransmittalTemplate.doc) — defining visual element is the
 * 11-action checkbox grid + 5 SENT VIA options + items table.
 *
 * Backend contract unchanged. createTransmittal / updateTransmittal /
 * addTransmittalItems / removeTransmittalItem shapes match the existing
 * /api/transmittal endpoints.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Stack, Typography, Button, Alert, Box, Grid, TextField, MenuItem,
  Breadcrumbs, Link as MuiLink, IconButton, Divider,
  Checkbox, FormControlLabel, Paper,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DeleteOutlineRounded, AddRounded,
  ForwardToInboxOutlined,
} from "@mui/icons-material";

import {
  getTransmittal, createTransmittal, updateTransmittal,
  addTransmittalItems, removeTransmittalItem,
  ALLOWED_SENT_VIA, ALLOWED_ACTIONS, ALLOWED_ITEM_TYPES,
} from "../../utils/transmittalsApi";

import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

const ACTION_LABELS = {
  APPROVAL:               "For approval",
  APPROVED_AS_SUBMITTED:  "Approved as submitted",
  APPROVED_AS_NOTED:      "Approved as noted",
  YOUR_USE:               "For your use",
  AS_REQUIRED:            "As required",
  RETURNED_AFTER_LOAN:    "Returned after loan",
  REVIEW_AND_COMMENT:     "Review and comment",
  RESUBMIT:               "Resubmit",
  SUBMIT:                 "Submit",
  RETURNED:               "Returned",
  CHANGE_ORDER:           "Change order",
};

const SENT_VIA_LABEL = (s) => s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Heuristic — split a stored "Acme Steel · ATTN: Joe Smith" string back
// into Company / ATTN parts, mirroring the RfiForm pattern. Schema only
// has recipient_party today; ATTN is captured client-side and combined
// on save. Round-trip parser keeps existing rows working.
function splitRecipient(combined) {
  if (!combined) return { company: "", attn: "" };
  const m = combined.match(/^(.*?)\s*[·•|]\s*ATTN:?\s*(.+)$/i);
  if (m) return { company: m[1].trim(), attn: m[2].trim() };
  const m2 = combined.match(/^(.*?),\s*ATTN:?\s*(.+)$/i);
  if (m2) return { company: m2[1].trim(), attn: m2[2].trim() };
  return { company: combined, attn: "" };
}

function combineRecipient(company, attn) {
  const c = (company || "").trim();
  const a = (attn || "").trim();
  if (c && a) return `${c} · ATTN: ${a}`;
  return c || a || "";
}

export default function TransmittalForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isCreate = !id || id === "new";
  const jobIdParam = params.get("job_id") || "";

  const [tr, setTr] = useState(null);
  const [jobId, setJobId] = useState(jobIdParam);
  const [recipientCompany, setRecipientCompany] = useState("");
  const [recipientAttn, setRecipientAttn] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sentVia, setSentVia] = useState("EMAIL");
  const [sentViaOther, setSentViaOther] = useState("");
  const [actionRequested, setActionRequested] = useState([]);
  const [notes, setNotes] = useState("");

  const [items, setItems] = useState([]);
  const [draftItem, setDraftItem] = useState({
    item_type: "DRAWING", document_id: "", item_description: "", copies: 1,
  });

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const refresh = () => {
    if (isCreate) return;
    setLoading(true);
    getTransmittal(id)
      .then((r) => {
        setTr(r);
        setJobId(String(r.job_id || ""));
        const split = splitRecipient(r.recipient_party || "");
        setRecipientCompany(split.company);
        setRecipientAttn(split.attn);
        setRecipientAddress(r.recipient_address || "");
        setRecipientEmail(r.recipient_email || "");
        setSentVia(r.sent_via || "EMAIL");
        setSentViaOther(r.sent_via_other_text || "");
        setActionRequested(Array.isArray(r.action_requested) ? r.action_requested : []);
        setNotes(r.notes || "");
        setItems(r.items || []);
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id, isCreate]);

  const toggleAction = (action) => {
    setActionRequested((prev) =>
      prev.includes(action) ? prev.filter((a) => a !== action) : [...prev, action]
    );
  };

  const handleAddDraftItem = () => {
    if (!draftItem.document_id && !draftItem.item_description) {
      setError("An item needs either a Document ID or a Description.");
      return;
    }
    setItems((prev) => [...prev, { ...draftItem, _local: true, _id: Date.now() }]);
    setDraftItem({ item_type: "DRAWING", document_id: "", item_description: "", copies: 1 });
    setError(null);
  };

  const handleRemoveDraftItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemovePersistedItem = async (itemId) => {
    setSaving(true);
    try {
      await removeTransmittalItem(id, itemId);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!jobId) {
      setError("Job No. is required.");
      return;
    }
    if (!recipientCompany.trim()) {
      setError("Recipient Company / Party is required.");
      return;
    }
    if (!actionRequested.length) {
      setError("At least one action must be selected — pick all that apply from the Action Requested grid.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        job_id: Number(jobId),
        recipient_party: combineRecipient(recipientCompany, recipientAttn),
        recipient_address: recipientAddress || null,
        recipient_email: recipientEmail || null,
        sent_via: sentVia,
        sent_via_other_text: sentVia === "OTHER" ? (sentViaOther || null) : null,
        action_requested: actionRequested,
        notes: notes || null,
      };
      if (isCreate) {
        const localItems = items
          .filter((it) => it._local)
          .map((it) => ({
            item_type: it.item_type,
            document_id: it.document_id ? Number(it.document_id) : null,
            item_description: it.item_description || null,
            copies: Number(it.copies) || 1,
          }));
        const created = await createTransmittal({ ...payload, items: localItems });
        navigate(`/transmittals/${created.id}`);
      } else {
        const { job_id: _ignore, ...editable } = payload;
        await updateTransmittal(id, editable);
        const localItems = items
          .filter((it) => it._local)
          .map((it) => ({
            item_type: it.item_type,
            document_id: it.document_id ? Number(it.document_id) : null,
            item_description: it.item_description || null,
            copies: Number(it.copies) || 1,
          }));
        if (localItems.length) await addTransmittalItems(id, localItems);
        refresh();
      }
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message);
    }
  };

  const status = tr?.status || "DRAFT";
  const canEdit = isCreate || status === "DRAFT";

  // Display values for the right header column
  const transmittalNumber = tr?.transmittal_number || (isCreate ? "Assigned automatically" : `#${id}`);
  const transmittalDate = useMemo(() => {
    if (tr?.sent_at) return new Date(tr.sent_at).toLocaleDateString();
    if (tr?.created_at) return new Date(tr.created_at).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [tr]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading transmittal…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isCreate ? "New Transmittal" : `Transmittal ${tr?.transmittal_number || ""}`} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          {/* Breadcrumbs + back nav */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" />
                  <span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/transmittals" color="inherit" underline="hover">Transmittals</MuiLink>
              <Typography color="text.primary">
                {isCreate ? "New" : (tr?.transmittal_number || `#${id}`)}
              </Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/transmittals")} aria-label="Back to Transmittal list">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!isCreate && status !== "DRAFT" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This transmittal is in <strong>{status}</strong> state — fields are read-only.
              Use the detail page to record return / close actions.
            </Alert>
          )}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Transmittal"
            title="Transmittal"
            subtitle="AISC §1.7.0 — outgoing transmittal cover sheet"
            status={status}
            number={transmittalNumber}
            numberLabel="Transmittal No."
            date={transmittalDate}
          />

          {/* TWO-COLUMN METADATA GRID */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="To (Recipient)" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Company / Party</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    placeholder="e.g., New England Infrastructure, Inc."
                    value={recipientCompany}
                    onChange={(e) => setRecipientCompany(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
                <Box>
                  <FieldLabel>Attention</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    placeholder="e.g., William Dube — Project Manager"
                    value={recipientAttn}
                    onChange={(e) => setRecipientAttn(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
                <Box>
                  <FieldLabel>Address</FieldLabel>
                  <TextField
                    fullWidth size="small" multiline rows={2}
                    placeholder="Street, City, State ZIP"
                    value={recipientAddress}
                    onChange={(e) => setRecipientAddress(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
                <Box>
                  <FieldLabel>Email</FieldLabel>
                  <TextField
                    fullWidth size="small" type="email"
                    placeholder="recipient@example.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection label="Project & Delivery" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Job No.</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    placeholder="e.g., 24-101"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    disabled={!isCreate}
                    helperText={isCreate ? null : "Job is locked once a transmittal is created."}
                  />
                </Box>
                <Box>
                  <FieldLabel required>Sent Via</FieldLabel>
                  <TextField
                    select fullWidth size="small"
                    value={sentVia}
                    onChange={(e) => setSentVia(e.target.value)}
                    disabled={!canEdit}
                  >
                    {ALLOWED_SENT_VIA.map((s) => (
                      <MenuItem key={s} value={s}>{SENT_VIA_LABEL(s)}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {sentVia === "OTHER" && (
                  <Box>
                    <FieldLabel>Other (specify)</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      placeholder="e.g., FedEx Overnight"
                      value={sentViaOther}
                      onChange={(e) => setSentViaOther(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Box>
                )}
              </PaperFormSection>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* ACTION REQUESTED — 11-CHECKBOX GRID (defining paper-form element) */}
          <PaperFormSection
            label="Action Requested"
            hint="select all that apply *"
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "#fafafa",
              }}
            >
              {ALLOWED_ACTIONS.map((a) => (
                <FormControlLabel
                  key={a}
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={actionRequested.includes(a)}
                      onChange={() => toggleAction(a)}
                      disabled={!canEdit}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {ACTION_LABELS[a] || a}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </PaperFormSection>

          {/* ITEMS TABLE */}
          <PaperFormSection label="Items Transmitted">
            <Table
              size="small"
              sx={{
                "& th": {
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: "text.secondary",
                  bgcolor: "#fafafa", borderBottom: "1px solid", borderColor: "divider",
                },
                "& td": { fontSize: 13 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 130 }}>Type</TableCell>
                  <TableCell sx={{ width: 90 }}>Doc ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Copies</TableCell>
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: "text.secondary", fontStyle: "italic", py: 2 }}>
                      No items yet — add at least one row below.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((it, idx) => (
                  <TableRow key={it.id || it._id} hover>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {(it.item_type || "").replace(/_/g, " ")}
                      </Typography>
                    </TableCell>
                    <TableCell>{it.document_id || "—"}</TableCell>
                    <TableCell>{it.item_description || "—"}</TableCell>
                    <TableCell align="right">{it.copies}</TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton
                          size="small"
                          disabled={saving}
                          onClick={() => it._local
                            ? handleRemoveDraftItem(idx)
                            : handleRemovePersistedItem(it.id)}
                          aria-label="Remove item"
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {canEdit && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2, mt: 1,
                  bgcolor: "#fafafa",
                  borderStyle: "dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-end" }}>
                  <Box sx={{ minWidth: 130 }}>
                    <FieldLabel>Type</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      value={draftItem.item_type}
                      onChange={(e) => setDraftItem({ ...draftItem, item_type: e.target.value })}
                    >
                      {ALLOWED_ITEM_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ minWidth: 90 }}>
                    <FieldLabel>Doc ID</FieldLabel>
                    <TextField
                      fullWidth size="small" type="number"
                      placeholder="optional"
                      value={draftItem.document_id}
                      onChange={(e) => setDraftItem({ ...draftItem, document_id: e.target.value })}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <FieldLabel>Description</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      placeholder="e.g., DWG SD-1 Rev B"
                      value={draftItem.item_description}
                      onChange={(e) => setDraftItem({ ...draftItem, item_description: e.target.value })}
                    />
                  </Box>
                  <Box sx={{ minWidth: 80 }}>
                    <FieldLabel>Copies</FieldLabel>
                    <TextField
                      fullWidth size="small" type="number"
                      value={draftItem.copies}
                      onChange={(e) => setDraftItem({ ...draftItem, copies: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="outlined" startIcon={<AddRounded />}
                      onClick={handleAddDraftItem}
                      sx={{ textTransform: "none", borderRadius: 2, height: 40 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}
          </PaperFormSection>

          {/* NOTES */}
          <PaperFormSection
            label="Notes"
            hint="optional · context, special instructions, reference numbers"
          >
            <TextField
              fullWidth multiline rows={3}
              placeholder="e.g., Please confirm receipt by COB Friday. Drawings supersede prior issue dated 2026-04-22."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
            />
          </PaperFormSection>

          <PaperFormFooter
            closingText="Returned to sender — please acknowledge receipt and indicate disposition above."
            citation="AISC §1.7.0 — drawing transmittal"
          />
        </PaperFormCard>

        {/* ACTION BAR */}
        {canEdit && (
          <Box sx={{ maxWidth: "md", mx: "auto", px: { xs: 2, md: 3 }, pb: 6 }}>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="text"
                onClick={() => navigate("/transmittals")}
                disabled={saving}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<ForwardToInboxOutlined />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {saving ? "Saving…" : (isCreate ? "Create Transmittal" : "Save changes")}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}
